import React from "react";
import { Button, Text, makeStyles, mergeClasses, tokens, Spinner } from "@fluentui/react-components";
import {
  CheckmarkCircle24Filled,
  DismissCircle24Filled,
  CircleRegular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { EditProposal } from "../../../../../contexts/SettingsContext";
import { applyProposals, readCurrentValues, isExcelHost } from "../../../../../utils/EditProposals";

const useStyles = makeStyles({
  card: {
    marginTop: "12px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "8px",
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    gap: "8px",
    flexWrap: "wrap",
  },
  headerActions: {
    display: "flex",
    gap: "6px",
  },
  diffRow: {
    display: "flex",
    alignItems: "flex-start",
    padding: "8px 12px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: "8px",
    flexWrap: "wrap",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  statusIcon: {
    marginTop: "2px",
  },
  content: {
    flex: "1",
    minWidth: "200px",
  },
  address: {
    fontFamily: '"Cascadia Mono", "Consolas", "Courier New", monospace',
    fontWeight: 600,
    fontSize: "12px",
  },
  line: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    fontFamily: '"Cascadia Mono", "Consolas", "Courier New", monospace',
    fontSize: "12px",
    lineHeight: "1.6",
    wordBreak: "break-word",
  },
  removed: {
    color: tokens.colorPaletteRedForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: "3px",
    padding: "0 4px",
    marginTop: "2px",
  },
  added: {
    color: tokens.colorPaletteGreenForeground1,
    backgroundColor: tokens.colorPaletteGreenBackground1,
    borderRadius: "3px",
    padding: "0 4px",
    marginTop: "2px",
  },
  actions: {
    display: "flex",
    gap: "6px",
    alignItems: "center",
  },
  statusText: {
    fontSize: "11px",
    fontWeight: 600,
  },
  appliedText: {
    color: tokens.colorPaletteGreenForeground1,
  },
  skippedText: {
    color: tokens.colorNeutralForeground3,
  },
  note: {
    padding: "8px 12px",
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  emptyCell: {
    color: tokens.colorNeutralForeground3,
  },
});

interface EditProposalsCardProps {
  messageId?: string;
  proposals: EditProposal[];
  onProposalsChange?: (messageId: string, proposals: EditProposal[]) => void;
}

const EditProposalsCard: React.FC<EditProposalsCardProps> = ({
  messageId,
  proposals,
  onProposalsChange,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [currentValues, setCurrentValues] = React.useState<Record<string, string>>({});
  const [applying, setApplying] = React.useState(false);

  const pending = proposals.filter((p) => p.status === "pending");

  React.useEffect(() => {
    let cancelled = false;
    if (isExcelHost()) {
      const addresses = proposals.map((p) => p.address);
      readCurrentValues(addresses).then((values) => {
        if (!cancelled) setCurrentValues(values);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [proposals]);

  const updateProposals = (next: EditProposal[]) => {
    if (messageId && onProposalsChange) {
      onProposalsChange(messageId, next);
    }
  };

  const handleApplyOne = async (proposal: EditProposal) => {
    if (applying) return;
    setApplying(true);
    try {
      if (isExcelHost()) {
        await applyProposals([proposal]);
      }
      updateProposals(
        proposals.map((p) => (p.id === proposal.id ? { ...p, status: "applied" } : p))
      );
    } catch (e) {
      console.error("Failed to apply proposal:", e);
    } finally {
      setApplying(false);
    }
  };

  const handleSkipOne = (proposal: EditProposal) => {
    updateProposals(proposals.map((p) => (p.id === proposal.id ? { ...p, status: "skipped" } : p)));
  };

  const handleApplyAll = async () => {
    if (applying || pending.length === 0) return;
    setApplying(true);
    try {
      if (isExcelHost()) {
        await applyProposals(pending);
      }
      updateProposals(
        proposals.map((p) => (p.status === "pending" ? { ...p, status: "applied" } : p))
      );
    } catch (e) {
      console.error("Failed to apply proposals:", e);
    } finally {
      setApplying(false);
    }
  };

  const handleSkipAll = () => {
    updateProposals(
      proposals.map((p) => (p.status === "pending" ? { ...p, status: "skipped" } : p))
    );
  };

  const displayValue = (value: string): string => {
    const trimmed = (value || "").trim();
    return trimmed === "" ? t("chat.proposalEmpty", "empty") : trimmed;
  };

  const StatusIcon: React.FC<{ status: EditProposal["status"] }> = ({ status }) => {
    if (status === "applied") {
      return (
        <CheckmarkCircle24Filled
          className={styles.statusIcon}
          style={{ color: tokens.colorPaletteGreenForeground1 }}
        />
      );
    }
    if (status === "skipped") {
      return (
        <DismissCircle24Filled
          className={styles.statusIcon}
          style={{ color: tokens.colorNeutralForeground3 }}
        />
      );
    }
    return (
      <CircleRegular
        className={styles.statusIcon}
        style={{ color: tokens.colorNeutralForeground3 }}
      />
    );
  };

  if (!proposals || proposals.length === 0) return null;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Text size={200} weight="semibold">
          {t("chat.proposalsTitle", "Proposed changes")} ({proposals.length})
        </Text>
        <div className={styles.headerActions}>
          <Button
            size="small"
            appearance="primary"
            onClick={handleApplyAll}
            disabled={applying || pending.length === 0}
          >
            {t("chat.allowAll", "Allow all")}
          </Button>
          <Button
            size="small"
            appearance="secondary"
            onClick={handleSkipAll}
            disabled={pending.length === 0}
          >
            {t("chat.skipAll", "Skip all")}
          </Button>
        </div>
      </div>

      {proposals.map((proposal) => {
        const oldValue = currentValues[proposal.address] ?? "";
        const isPending = proposal.status === "pending";
        return (
          <div key={proposal.id} className={styles.diffRow}>
            <StatusIcon status={proposal.status} />
            <div className={styles.content}>
              <div className={styles.address}>{proposal.address}</div>
              <div className={styles.line}>
                <Text size={100} className={styles.removed}>
                  − {displayValue(oldValue)}
                </Text>
              </div>
              <div className={styles.line}>
                <Text size={100} className={styles.added}>
                  + {displayValue(proposal.value)}
                </Text>
              </div>
            </div>
            <div className={styles.actions}>
              {isPending ? (
                <>
                  <Button
                    size="small"
                    appearance="primary"
                    icon={<CheckmarkCircle24Filled />}
                    onClick={() => handleApplyOne(proposal)}
                    disabled={applying}
                  >
                    {t("chat.allow", "Allow")}
                  </Button>
                  <Button
                    size="small"
                    appearance="secondary"
                    icon={<DismissCircle24Filled />}
                    onClick={() => handleSkipOne(proposal)}
                  >
                    {t("chat.skip", "Skip")}
                  </Button>
                </>
              ) : (
                <Text
                  size={100}
                  className={mergeClasses(
                    styles.statusText,
                    proposal.status === "applied" ? styles.appliedText : styles.skippedText
                  )}
                >
                  {proposal.status === "applied"
                    ? t("chat.applied", "Applied")
                    : t("chat.skipped", "Skipped")}
                </Text>
              )}
            </div>
          </div>
        );
      })}

      {applying && (
        <div className={styles.note}>
          <Spinner size="tiny" /> {t("chat.applying", "Applying changes...")}
        </div>
      )}
    </div>
  );
};

export default EditProposalsCard;
